// Supply Network panel: filters row for the "Non-app dealer locations"
// tab. Pure presentational — all state lives in the hook. Search has
// been lifted out to the tabbed wrapper so a single search field
// drives both tabs (app-using + non-app); the remaining four filters
// stay local to this tab because the app-using side has a different
// filter shape (subscriber-org instead of linked-org).
//
// Renders inline (no SectionCard) so it sits as a compact filter row
// inside the unified Dealer-locations tabbed box.
import { mfgFilterGhostBtnClass, mfgXsInputTallClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  LINKED_ORG_FILTER_UNLINKED,
  PLANT_FILTER_UNASSIGNED,
  type UseManufacturingPlantDealerLocations,
} from "./useManufacturingPlantDealerLocations";

export function ManufacturingPlantDealerLocationsFilters({
  m,
}: {
  m: UseManufacturingPlantDealerLocations;
}) {
  const anyFilterActive =
    m.filterPlant !== "" ||
    m.filterLinkedOrg !== "" ||
    m.filterState.trim().length > 0 ||
    m.filterActive !== "active";

  function resetFilters() {
    m.setFilterPlant("");
    m.setFilterLinkedOrg("");
    m.setFilterState("");
    m.setFilterActive("active");
  }

  // Slice 10: filter row now inline (no bordered wrapper) with
  // gap-x-4 + flex-col labels so each label↔select pair has clear
  // vertical separation and adjacent pairs don't smash together.
  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
        Plant
        <select
          className={mfgXsInputTallClass}
          value={m.filterPlant}
          onChange={(e) => m.setFilterPlant(e.target.value)}
        >
          <option value="">Any plant</option>
          <option value={PLANT_FILTER_UNASSIGNED}>Unassigned (no plant)</option>
          {m.plants
            .filter((p) => p.is_active)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.plant_code} · {p.name}
              </option>
            ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
        Linked dealer org
        <select
          className={mfgXsInputTallClass}
          value={m.filterLinkedOrg}
          onChange={(e) => m.setFilterLinkedOrg(e.target.value)}
        >
          <option value="">Any (linked or not)</option>
          <option value={LINKED_ORG_FILTER_UNLINKED}>Unlinked (non-app dealer)</option>
          {m.subscriberDealers.map((s) => (
            <option key={s.dealer_org_id} value={s.dealer_org_id}>
              {s.dealer_org_name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
        State
        <select
          className={mfgXsInputTallClass}
          value={m.filterState}
          onChange={(e) => m.setFilterState(e.target.value)}
        >
          <option value="">Any state</option>
          {m.knownStates.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-zinc-300">
        Show
        <select
          className={mfgXsInputTallClass}
          value={m.filterActive}
          onChange={(e) =>
            m.setFilterActive(e.target.value === "all" ? "all" : "active")
          }
        >
          <option value="active">Active only</option>
          <option value="all">Show all (incl. inactive)</option>
        </select>
      </label>
      <div className="ml-auto">
        {anyFilterActive ? (
          <button type="button" className={mfgFilterGhostBtnClass} onClick={resetFilters}>
            Clear filters
          </button>
        ) : null}
      </div>
    </div>
  );
}
